import { SEOHead } from "@/features/shared/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MapPin, ArrowRight, Globe, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { FrayzeLogo } from "@/components/FrayzeLogo";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";

export default function ServiceAreas() {
    const serviceAreas = [
        {
            name: "Thunder Bay",
            description: "Our HQ. Helping local businesses dominate the Northern Ontario market.",
            href: "/service-areas/thunder-bay/"
        },
        {
            name: "Toronto",
            description: "Scalable growth systems for competitive GTA markets.",
            href: "/service-areas/toronto/"
        },
        {
            name: "Sudbury",
            description: "Modernizing mining, industrial, and service sectors with AI.",
            href: "/service-areas/sudbury/"
        },
        // Placeholders for future expansion or general service areas
        {
            name: "Ottawa",
            description: "Government-grade compliance and enterprise automation.",
            href: "/contact/"
        },
        {
            name: "Northern Ontario",
            description: "Connecting the North with world-class digital infrastructure.",
            href: "/contact/"
        }
    ];

    return (
        <>
            <SEOHead
                title="AI Automation Ontario | CRM & Lead Systems by Region | Frayze"
                description="AI automation, CRM & lead systems for businesses across Ontario: from Thunder Bay and Sudbury to the GTA. Run remotely from our Thunder Bay HQ. Book a free strategy call."
                keywords="AI automation ontario, ontario CRM, AI-powered CRM, lead generation ontario, automation systems, Canadian businesses, service areas, Ontario, Manitoba, business growth"
                canonical="https://frayze.ca/service-areas/"
            />

            <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <main className="flex-1 pt-20">
                    {/* Hero Section */}
                    <section className="relative py-24 lg:py-32 bg-background border-b overflow-hidden">
                        <div className="absolute inset-0 bg-grid-primary/5 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
                        <div className="container relative mx-auto px-6 text-center">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                                <Globe className="h-4 w-4" />
                                <span>Served from Thunder Bay, ON</span>
                            </div>
                            <h1 className="text-4xl lg:text-6xl font-bold tracking-tight mb-6 max-w-4xl mx-auto">
                                AI Automation & CRM Systems <br className="hidden lg:block" />
                                <span className="text-primary">Across Ontario & Beyond</span>
                            </h1>
                            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
                                Get more leads, less busywork, and systems that scale. We're a 100% Canadian-owned growth platform that helps you modernize, automate, and grow, no matter where you operate.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <Button size="lg" asChild className="shadow-lg hover:shadow-xl transition-all">
                                    <Link to="/digital-audit/">
                                        Get Your Automation Score
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button size="lg" variant="outline" asChild>
                                    <Link to="/contact/">
                                        Book a Strategy Call
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </section>

                    {/* Location Grid */}
                    <section className="py-24 bg-muted/30">
                        <div className="container mx-auto px-6">
                            <div className="text-center max-w-3xl mx-auto mb-16">
                                <h2 className="text-3xl font-bold mb-4">Where We Work</h2>
                                <p className="text-muted-foreground">
                                    We work digitally across North America and build deep local market knowledge for these key Canadian regions. Find yours below.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {serviceAreas.map((area) => (
                                    <Card key={area.name} className="hover:shadow-lg transition-all duration-300 border-primary/10 hover:border-primary/30 group bg-background">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-3">
                                                <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                                    <MapPin className="h-5 w-5" />
                                                </div>
                                                {area.name}
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <CardDescription className="text-base">
                                                {area.description}
                                            </CardDescription>
                                        </CardContent>
                                        <CardFooter>
                                            <Button variant="ghost" className="p-0 h-auto hover:bg-transparent hover:text-primary group-hover:translate-x-1 transition-all" asChild>
                                                <Link to={area.href} className="flex items-center gap-2">
                                                    View Regional Services <ArrowRight className="h-4 w-4" />
                                                </Link>
                                            </Button>
                                        </CardFooter>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Remote/Digital Section */}
                    <section className="py-24 bg-background">
                        <div className="container mx-auto px-6">
                            <div className="bg-primary/5 rounded-3xl p-8 lg:p-12 border border-primary/10">
                                <div className="grid lg:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <h2 className="text-3xl font-bold mb-4">Remote-First, Security-First</h2>
                                        <p className="text-muted-foreground mb-6">
                                            Our growth systems are cloud-native and deployable anywhere. Whether you are in a major metro like Vancouver or a remote community in Northern Ontario,
                                            Frayze provides the same enterprise-grade infrastructure, SOC 2 compliance, and 24/7 reliability.
                                        </p>
                                        <ul className="space-y-3 mb-8">
                                            {[
                                                "100% Remote Onboarding & Implementation",
                                                "Digital-First Communication & Support",
                                                "Compliance with PIPEDA & Canadian Data Laws",
                                                "Secure Cloud Infrastructure (AWS/Google Canada)"
                                            ].map((item, i) => (
                                                <li key={i} className="flex items-center gap-3 text-sm font-medium">
                                                    <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                        <Button asChild>
                                            <Link to="/contact/">Contact Our Team</Link>
                                        </Button>
                                    </div>
                                    <div className="relative h-64 lg:h-full min-h-[300px] rounded-2xl overflow-hidden bg-background border flex items-center justify-center">
                                        <div className="absolute inset-0 bg-grid-black/5 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
                                        <div className="text-center p-6">
                                            <TrendingUp className="h-16 w-16 text-primary mx-auto mb-4 opacity-80" />
                                            <div className="text-2xl font-bold opacity-80">Growth Has No Borders</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    {/* FAQ Section */}
                    <section className="py-24 bg-muted/20">
                        <div className="container mx-auto px-6 max-w-4xl">
                            <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
                            <Accordion type="single" collapsible className="w-full">
                                <AccordionItem value="item-1">
                                    <AccordionTrigger>I’m not in Thunder Bay. Can we still work together?</AccordionTrigger>
                                    <AccordionContent>
                                        Absolutely. Frayze is a digital-first agency. Whether you are in Toronto, Calgary, or a remote fly-in community, our entire process. From strategy to implementation. Is designed to be handled remotely. We use Zoom, Slack, and our own project portals to keep you connected.
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value="item-2">
                                    <AccordionTrigger>Is my data stored in Canada?</AccordionTrigger>
                                    <AccordionContent>
                                        Yes. We prioritize Canadian data sovereignty. Our infrastructure and recommended CRM configurations are designed to comply with PIPEDA. We work with data centers in Montreal and Toronto to ensure your customer data stays on Canadian soil whenever possible.
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value="item-3">
                                    <AccordionTrigger>Do you have experience with my specific industry?</AccordionTrigger>
                                    <AccordionContent>
                                        We specialize in service-based businesses, trades, automotive, and professional services. However, our "Growth System" methodology is universal: we build infrastructure to attract leads, automate follow-ups, and close sales. If you have customers, our system works.
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value="item-4">
                                    <AccordionTrigger>How does implementation work if you aren't on-site?</AccordionTrigger>
                                    <AccordionContent>
                                        We don't need to be in your office to build your digital infrastructure. We gain access to your current software accounts securely, migrate your data, and build your new systems in the cloud. We provide comprehensive video training and live Zoom onboarding sessions for your team.
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        </>
    );
}
